import {
    ref, nextTick, type Ref
} from 'vue';
import { formatQueryParamDate } from '@agyletime/vue-3-common/src/utils/date';
import {
    convert12to24,
    convert24to12,
    extractTime,
    normalizeTime,
    convertToUtcIso
} from '@/components/TeamAndSchedule/utils/timeConversion';
import {
    validateHours as validateHoursUtil,
    validateMinutes as validateMinutesUtil,
    handleHoursInput
} from '@/components/TeamAndSchedule/utils/timeValidation';
import { parseFacetsFromUrl } from '@/components/TeamAndSchedule/utils/facetUtils';
import type { FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';

interface UseTimeRangeOptions {
    facetId: string;
    facetName: string;
    urlKey?: string;
    activeTable: string;
    userTimezone: Ref<string>;
    emit: (event: 'change', payload: TimeRangeChangePayload) => void;
}

interface TimeRangeChangePayload {
    value: string[];
    facetId: string;
    facetName: string;
    urlKey: string;
    boundaries: string;
    bdField: string | undefined;
    shouldUpdate: boolean;
}

/**
 * Composable for managing time range state and operations
 * Handles 12-hour format time inputs with AM/PM selection
 */
export const useTimeRange = (options: UseTimeRangeOptions) => {
    const { facetId, facetName, urlKey, activeTable, userTimezone, emit } = options;

    // 12-hour format state (numeric values for logic)
    const startHours = ref<number | null>(null);
    const startMinutes = ref<number | null>(null);
    const startPeriod = ref<'AM' | 'PM'>('AM');

    const endHours = ref<number | null>(null);
    const endMinutes = ref<number | null>(null);
    const endPeriod = ref<'AM' | 'PM'>('AM');

    // Display values (string values for preserving user input like "00")
    const startMinutesDisplay = ref<string>('');
    const endMinutesDisplay = ref<string>('');

    // Input element refs for auto-advance
    const startMinutesInputRef = ref<HTMLInputElement | null>(null);
    const endMinutesInputRef = ref<HTMLInputElement | null>(null);

    // Field mapping for cleaner code
    const fieldMap: Record<string, Ref<number | null>> = {
        startHours,
        startMinutes,
        endHours,
        endMinutes
    };

    /**
     * Emit time change event with formatted data
     */
    const onChangeTime = () => {
        // Convert 12-hour format to 24-hour HH:mm strings
        const startTime24 = convert12to24(
            startHours.value,
            startMinutes.value,
            startPeriod.value
        );
        const endTime24 = convert12to24(
            endHours.value,
            endMinutes.value,
            endPeriod.value
        );

        const start =
            startTime24 &&
            formatQueryParamDate(
                JSON.parse(JSON.stringify(normalizeTime(startTime24))),
                userTimezone.value
            );
        const end =
            endTime24 &&
            formatQueryParamDate(
                JSON.parse(JSON.stringify(normalizeTime(endTime24))),
                userTimezone.value
            );

        const timeRangeValue =
            startTime24 || endTime24
                ? [
                    convertToUtcIso(normalizeTime(startTime24)),
                    convertToUtcIso(normalizeTime(endTime24))
                ]
                : [];

        emit('change', {
            value: timeRangeValue,
            facetId,
            facetName,
            urlKey: `${urlKey}__$range`,
            boundaries: start || end ? `${start}_${end}` : '',
            bdField: urlKey,
            shouldUpdate: true
        });
    };

    /**
     * Apply smart validation to minutes value
     */
    const applyMinutesValidation = (value: string): string => {
        if (!value) return '';
        
        const numValue = parseInt(value, 10);
        
        // Single digit 6-9 is complete (can't be 60-90)
        if (value.length === 1 && numValue >= 6) {
            return numValue.toString();
        }
        
        // Clamp to 59 if two digits exceed limit
        if (value.length === 2 && numValue > 59) {
            return '59';
        }
        
        return value;
    };

    /**
     * Update display and numeric value for minutes field
     */
    const updateMinutesValue = (
        field: string,
        value: string,
        fieldRef: Ref<number | null>
    ): void => {
        const isStartMinutes = field === 'startMinutes';
        const displayRef = isStartMinutes ? startMinutesDisplay : endMinutesDisplay;
        
        if (!value) {
            displayRef.value = '';
            fieldRef.value = null;
            return;
        }
        
        const validatedValue = applyMinutesValidation(value);
        displayRef.value = validatedValue;
        fieldRef.value = parseInt(validatedValue, 10);
    };

    /**
     * Check if any time value is set
     */
    const hasAnyValueSet = (): boolean => {
        return startHours.value !== null || startMinutes.value !== null ||
               endHours.value !== null || endMinutes.value !== null;
    };

    /**
     * Check if change should be emitted based on current state
     */
    const shouldEmitChange = (): boolean => {
        const hasCompleteStartTime = startHours.value !== null && startMinutes.value !== null;
        const hasCompleteEndTime = endHours.value !== null && endMinutes.value !== null;
        
        // Emit if we have at least one complete time
        if (hasCompleteStartTime || hasCompleteEndTime) {
            return true;
        }
        
        // Also emit if nothing is set (to clear the URL)
        return !hasAnyValueSet();
    };

    /**
     * Handle input changes for minutes with display value tracking
     * Validates and clamps minutes to 0-59 range in real-time
     */
    const handleInput = (field: string, event: Event) => {
        const target = event.target as HTMLInputElement;
        const value = target.value.replace(/\D/g, '').slice(0, 2);
        
        const fieldRef = fieldMap[field];
        if (!fieldRef) return;

        // Handle minutes fields with smart validation
        if (field === 'startMinutes' || field === 'endMinutes') {
            updateMinutesValue(field, value, fieldRef);
        } else {
            // Handle other fields (hours)
            fieldRef.value = value === '' ? null : parseInt(value, 10);
        }
        
        // Emit change only if appropriate
        if (shouldEmitChange()) {
            onChangeTime();
        }
    };

    /**
     * Handle smart input for hours with auto-advance
     * Only triggers API call if BOTH hours and minutes are complete
     */
    const handleHoursInputSmart = (type: 'start' | 'end', event: Event) => {
        const hoursRef = type === 'start' ? startHours : endHours;
        const minutesRef = type === 'start' ? startMinutes : endMinutes;
        const minutesInputRef = type === 'start' ? startMinutesInputRef : endMinutesInputRef;

        // Pass onChange handler that waits for Vue reactivity to update
        handleHoursInput(event, {
            hoursRef,
            minutesRef,
            minutesInputRef,
            onChange: () => {
                // Use nextTick to ensure hoursRef.value has been updated
                nextTick(() => {
                    // Only emit change if we have COMPLETE time (hours + minutes)
                    if (shouldEmitChange()) {
                        onChangeTime();
                    }
                });
            }
        });
    };

    /**
     * Validate hours input (1-12)
     * Called on blur - triggers API call ONLY if time is complete (hours + minutes)
     */    /**
     * Validate hours input (1-12)
     * Called on blur - triggers API call ONLY if time is complete (hours + minutes)
     */
    const validateHours = (type: 'start' | 'end') => {
        const hoursRef = type === 'start' ? startHours : endHours;
        validateHoursUtil(hoursRef, () => {
            // Use nextTick to ensure hoursRef.value has been updated
            nextTick(() => {
                // Only emit change if we have COMPLETE time (hours + minutes)
                if (shouldEmitChange()) {
                    onChangeTime();
                }
            });
        });
    };

    /**
     * Validate minutes input (0-59)
     * Also updates display value after clamping
     * Called on blur - triggers API call ONLY if time is complete (hours + minutes)
     */
    const validateMinutes = (type: 'start' | 'end') => {
        const minutesRef = type === 'start' ? startMinutes : endMinutes;
        const displayRef = type === 'start' ? startMinutesDisplay : endMinutesDisplay;
        
        validateMinutesUtil(minutesRef, () => {
            // Use nextTick to ensure minutesRef.value has been updated
            nextTick(() => {
                // Only emit change if we have COMPLETE time (hours + minutes)
                if (shouldEmitChange()) {
                    onChangeTime();
                }
            });
        });
        
        // Update display value after validation
        if (minutesRef.value !== null) {
            displayRef.value = minutesRef.value.toString().padStart(2, '0');
        }
    };

    /**
     * Set time values from 12-hour format
     */
    const setTimeFrom12HourFormat = (
        time24: string,
        refs: {
            hours: Ref<number | null>;
            minutes: Ref<number | null>;
            minutesDisplay: Ref<string>;
            period: Ref<'AM' | 'PM'>;
        }
    ): void => {
        const time12 = convert24to12(time24);
        refs.hours.value = time12.hours;
        refs.minutes.value = time12.minutes;
        refs.minutesDisplay.value = time12.minutes !== null
            ? time12.minutes.toString().padStart(2, '0')
            : '';
        refs.period.value = time12.period;
    };

    /**
     * Load time range from URL parameters
     */
    const loadTimeRangeFromUrl = () => {
        const facets = parseFacetsFromUrl();
        if (!facets) return;

        const selectedRange = facets[activeTable]?.[facetId]?.value;
        if (!Array.isArray(selectedRange)) return;

        const [ startRaw, endRaw ] = selectedRange;

        // Extract and convert start time
        const startTime24 = startRaw ? extractTime(startRaw) : '';
        if (startTime24) {
            setTimeFrom12HourFormat(startTime24, {
                hours: startHours,
                minutes: startMinutes,
                minutesDisplay: startMinutesDisplay,
                period: startPeriod
            });
        }

        // Extract and convert end time
        const endTime24 = endRaw ? extractTime(endRaw) : '';
        if (endTime24) {
            setTimeFrom12HourFormat(endTime24, {
                hours: endHours,
                minutes: endMinutes,
                minutesDisplay: endMinutesDisplay,
                period: endPeriod
            });
        }
    };

    /**
     * Clear all time values
     */
    const clearAllTimeValues = (): void => {
        startHours.value = null;
        startMinutes.value = null;
        startMinutesDisplay.value = '';
        startPeriod.value = 'AM';
        endHours.value = null;
        endMinutes.value = null;
        endMinutesDisplay.value = '';
        endPeriod.value = 'AM';
    };

    /**
     * Handle selected view facet change
     */
    const onSelectedViewFacetChange = (value: unknown) => {
        const selectedViewValue = value as FacetItem;
        const [ start, end ] = selectedViewValue
            ? (selectedViewValue.value as [string, string])
            : [];

        if (!selectedViewValue?.value?.length) {
            clearAllTimeValues();
            return;
        }

        // Extract and convert times
        const startTime24 = start ? extractTime(start) : '';
        const endTime24 = end ? extractTime(end) : '';

        // Set or clear start time
        if (startTime24) {
            setTimeFrom12HourFormat(startTime24, {
                hours: startHours,
                minutes: startMinutes,
                minutesDisplay: startMinutesDisplay,
                period: startPeriod
            });
        } else {
            startHours.value = null;
            startMinutes.value = null;
            startMinutesDisplay.value = '';
            startPeriod.value = 'AM';
        }

        // Set or clear end time
        if (endTime24) {
            setTimeFrom12HourFormat(endTime24, {
                hours: endHours,
                minutes: endMinutes,
                minutesDisplay: endMinutesDisplay,
                period: endPeriod
            });
        } else {
            endHours.value = null;
            endMinutes.value = null;
            endMinutesDisplay.value = '';
            endPeriod.value = 'AM';
        }
    };

    return {
        // State
        startHours,
        startMinutes,
        startPeriod,
        endHours,
        endMinutes,
        endPeriod,

        // Display values
        startMinutesDisplay,
        endMinutesDisplay,

        // Input refs
        startMinutesInputRef,
        endMinutesInputRef,

        // Methods
        handleInput,
        handleHoursInputSmart,
        validateHours,
        validateMinutes,
        onChangeTime,
        loadTimeRangeFromUrl,
        onSelectedViewFacetChange
    };
};
